import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { S_LocationDot } from 'hosman-material';
import { updateAddressObj } from '../../../../../reducers/_Pages/Public/radarSlice';
import { initMapScript } from '../../../../../../shared/utils/googleMapsService';

const geocoderUrl = 'https://maps.googleapis.com/maps/api/geocode/json';

const RadarGeocoder = () => {
  const { addressObj } = useSelector((state) => state.radar);
  const [stateValue, setStateValue] = useState(addressObj.address || '');
  const [predictions, setPredictions] = useState([]);
  const [displayPredictions, setDisplayPredictions] = useState(false);
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  useEffect(() => {
    initMapScript().then(() => initAutocomplete())
  }, [stateValue]);

  useEffect(() => {
    setStateValue(addressObj.address || '')
  }, [addressObj]);

  useEffect(() => {
    // Close prediction list when clicking outside input
    window.onclick = () => {
      setDisplayPredictions(false)
    };
  }, []);

  function initAutocomplete() {
    if (stateValue.length < 2) setPredictions([])

    const displaySuggestions = function (predictions, status) {
      if (document.activeElement === inputRef.current) setDisplayPredictions(true)
      if (status != google.maps.places.PlacesServiceStatus.OK || !predictions) return;

      setPredictions(predictions.map(prediction => prediction.description))
    };

    const service = new google.maps.places.AutocompleteService();

    service.getPlacePredictions({ input: stateValue, types: ['address'], componentRestrictions: { country: 'fr' } }, displaySuggestions);
  }

  const selectOption = async (prediction) => {
    setDisplayPredictions(false)
    const result = await fetchAddressData(prediction)
    const addressObj = extractAddress(result)

    setStateValue(addressObj['address']);
    dispatch(updateAddressObj(addressObj));
  }

  const handleChange = (e) => {
    setStateValue(e.target.value)
    dispatch(updateAddressObj(({ ['address']: e.target.value })))
  }

  return (
    <div className='relative w-full' data-testid='RadarGeocoder'>
      <div className='w-full flex flex-col gap-3'>
        <input
          ref={inputRef}
          id='address'
          name='address'
          type='text'
          value={stateValue}
          onChange={handleChange}
          placeholder='Entrez une adresse...'
          autoComplete='off'
          className='block w-full h-12 bg-white/10 bg-opacity-30 rounded-2xl border-white/20 hover:border-white/40 p-3 focus:ring-0 focus:border-white/40 placeholder-white/40 pl-8 backdrop-blur-sm'
        />
      </div>

      <FontAwesomeIcon icon={S_LocationDot} className='absolute left-3 mt-0.5 text-lg top-3' />

      {displayPredictions && predictions.length > 0 &&
        <ul className='z-50 absolute left-auto top-auto w-auto max-w-none h-44 xl:h-auto bottom-14 rounded-2xl border border-ih-indigo overflow-y-scroll mt-2 bg-opacity-30 bg-ih-indigo/30 scrollbar-hide'>
          {predictions.map(prediction => {
            return (
              <li
                key={prediction}
                onClick={() => selectOption(prediction)}
                className='bg-ih-neutral-50 bg-opacity-30 bg-ih-indigo/30 hover:xl:bg-ih-indigo/50 first:rounded-t-2xl last:rounded-b-2xl cursor-pointer select-none relative py-3 px-3 border-b border-b-ih-indigo backdrop-blur-sm'
              >
                {prediction}
              </li>
            )
          })}
        </ul>
      }
    </div>
  );
};

function fetchAddressData(prediction) {
  const result = fetch(`${geocoderUrl}?address=${prediction}&region=fr&key=${process.env.GOOGLE_API_SERVER_KEY}`)

  return result.then((r) => r.json().then(jsonData => jsonData.results[0]));
}

function extractAddress(place) {
  const addressComponents = place.address_components
  const addressInfos = {
    'address': place.formatted_address,
    street_number: addressComponents.find(t => t.types[0].includes('street_number'))?.long_name,
    street_name: addressComponents.find(t => t.types[0].includes('route'))?.long_name,
    city: addressComponents.find(t => t.types[0].includes('locality'))?.long_name,
    zip_code: addressComponents.find(t => t.types[0].includes('postal_code'))?.long_name,
    country_code: addressComponents.find(t => t.types[0].includes('country'))?.short_name,
    lat: place.geometry.location.lat,
    lng: place.geometry.location.lng
  }

  return addressInfos;
}

export default RadarGeocoder;
